// frontend/src/pages/admin/CreateArticlePage.tsx
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, Eye, Code } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { createArticle, uploadArticleImage } from '@/api/articleService';
import { type ArticleCreatePayload } from '@/types';
import { getAllTags } from '@/api/tagService';
import { type TagPublic } from '@/types';

const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

const CreateArticlePage = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState(''); // Will store raw HTML
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  
  const [_imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null); // URL from backend
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [allTags, setAllTags] = useState<TagPublic[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Default to showing raw HTML view
  const [showRawHtml, setShowRawHtml] = useState(true); 

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tagsData = await getAllTags();
        setAllTags(tagsData);
      } catch (err) {
        console.error("Failed to fetch tags for create page:", err);
        setError("Could not load tags. Please try refreshing.");
      }
    };
    fetchTags();
  }, []);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    setSlug(slugify(newTitle));
  };

  const handleTagSelection = (tagId: string) => {
    const id = parseInt(tagId, 10);
    setSelectedTagIds(prev =>
      prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]
    );
  };
  
  // This function is called by RichTextEditor's onChange OR the textarea's onChange
  const handleBodyChange = (content: string) => {
    // If coming from RichTextEditor, 'content' is HTML.
    // If coming from textarea, 'content' is also the HTML string.
    setBody(content);
  };

  const handleImageFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Invalid file type. Please select an image.");
        setImageFile(null); setImagePreview(null); setUploadedImageUrl(null);
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError(null);
      setIsUploadingImage(true);
      try {
        const response = await uploadArticleImage(file);
        setUploadedImageUrl(response.image_url);
        console.log("Image uploaded, URL:", response.image_url);
      } catch (uploadError: any) {
        console.error("Failed to upload image:", uploadError);
        setError(uploadError.response?.data?.detail || "Image upload failed. Please try again.");
        setImagePreview(null); setUploadedImageUrl(null);
      } finally {
        setIsUploadingImage(false);
      }
    } else {
      setImageFile(null); setImagePreview(null); setUploadedImageUrl(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true); setError(null); setSuccessMessage(null);

    // The 'body' state already holds raw HTML, whether from Tiptap or the textarea
    if (!title || !slug || !body) {
      setError("Title, slug, and body are required.");
      setIsSubmitting(false); return;
    }

    const payload: ArticleCreatePayload = {
      title, slug, summary: summary || null, body, status,
      image_url: uploadedImageUrl, // Use the URL from the successful upload
      tag_ids: selectedTagIds.length > 0 ? selectedTagIds : null,
    };

    try {
      const newArticle = await createArticle(payload);
      setSuccessMessage(`Article "${newArticle.title}" created successfully! Redirecting...`);
      setTimeout(() => navigate('/admin/articles'), 2000);
    } catch (err: any) {
      console.error("Failed to create article:", err);
      if (err.response && err.response.data && err.response.data.detail) {
        // If detail is an array (common for Pydantic validation errors), join messages
        if (Array.isArray(err.response.data.detail)) {
          const messages = err.response.data.detail.map((item: any) => {
            let msg = item.msg;
            if (item.loc && item.loc.length > 1) {
              msg = `${item.loc.slice(1).join('.')} - ${msg}`;
            }
            return msg;
          }).join("; ");
          setError(`Validation Error: ${messages}`);
        } else if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else {
          setError("An unknown validation error occurred.");
        }
      } else {
        setError("Failed to create article. Please check your connection or server logs.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)} className="text-primary hover:text-primary/80 cursor-pointer">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Manage Articles
        </Button>
      </div>

      <Card className="max-w-3xl mx-auto bg-card border-border/30 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl md:text-3xl text-foreground">Create New Article</CardTitle>
          <CardDescription className="text-muted-foreground">Fill in the details below to publish a new article.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && ( <div className="p-3 bg-destructive/10 text-destructive border-destructive/30 rounded-md flex items-center"> <AlertCircle className="h-5 w-5 mr-2" /> {error} </div> )}
            {successMessage && ( <div className="p-3 bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 rounded-md flex items-center"> <CheckCircle className="h-5 w-5 mr-2" /> {successMessage} </div> )}
            
            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">Title</Label>
              <Input id="title" value={title} onChange={handleTitleChange} required disabled={isSubmitting} className="!bg-input"/>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug" className="text-foreground">Slug</Label>
              <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value)} required disabled={isSubmitting} className="!bg-input"/>
              <p className="text-xs text-muted-foreground">URL-friendly version of the title.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="summary" className="text-foreground">Summary (Optional)</Label>
              <Textarea id="summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="!bg-input min-h-[80px]" disabled={isSubmitting}/>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="body" className="text-foreground">Body</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowRawHtml(!showRawHtml)} className="text-xs cursor-pointer">
                  {showRawHtml ? <><Eye className="mr-1 h-3 w-3" /> View Rendered (Rich Text)</> : <><Code className="mr-1 h-3 w-3" /> View Raw HTML</>}
                </Button>
              </div>
              {showRawHtml ? (
                <Textarea
                  value={body} // Directly bind to body state
                  onChange={(e) => handleBodyChange(e.target.value)} // Update body state
                  className="w-full min-h-[250px] p-2 border-input rounded-md font-mono text-sm bg-muted/30"
                  placeholder="Enter raw HTML content..."
                  disabled={isSubmitting}
                />
              ) : (
                <div className="border-input rounded-md p-1 bg-background shadow-sm">
                  <RichTextEditor value={body} onChange={handleBodyChange} />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="articleImage" className="text-foreground">Article Image (Optional)</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="articleImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="!bg-input border-border focus:border-primary focus:ring-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  disabled={isUploadingImage || isSubmitting}
                />
                {isUploadingImage && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              </div>
              {imagePreview && (
                <div className="mt-2">
                  <img src={imagePreview} alt="Preview" className="h-32 w-auto object-contain border border-border rounded-md p-1 bg-muted/10" />
                  {uploadedImageUrl && <p className="text-xs text-green-600 mt-1">Image uploaded. Path: {uploadedImageUrl}</p>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="status" className="text-foreground">Status</Label>
                <Select value={status} onValueChange={(value: 'draft' | 'published' | 'archived') => setStatus(value)} disabled={isSubmitting}>
                  <SelectTrigger className="!bg-input"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 pb-4">
                <Label className="text-foreground">Tags (Optional)</Label>
                <div className="flex flex-wrap gap-2 p-3 border border-input rounded-md min-h-[40px] bg-background">
                    {allTags.length > 0 ? allTags.map(tag => (
                        <Button
                            type="button"
                            key={tag.id}
                            variant={selectedTagIds.includes(tag.id) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleTagSelection(String(tag.id))}
                            className={`transition-all cursor-pointer ${selectedTagIds.includes(tag.id) ? '!bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent/10'}`}
                            disabled={isSubmitting}
                        >
                            {tag.name}
                        </Button>
                    )) : <p className="text-sm text-muted-foreground">No tags available or loading...</p>}
                </div>
            </div>
          </CardContent>
          <CardFooter className="border-t border-border/30 pt-6">
            <Button type="submit" className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent" disabled={isSubmitting || isUploadingImage}>
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving Article...</> : <><Save className="mr-2 h-4 w-4" /> Save Article</>}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CreateArticlePage;
